import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress, InputAdornment, SxProps, TextField, TextFieldProps, Theme } from '@mui/material';
import React, { forwardRef, ForwardRefRenderFunction, useEffect, useRef, useState } from 'react';
import { IListSearchPayload, ListSearchMode } from './list.types';

interface IListSearchFieldProps extends Omit<TextFieldProps, 'onChange' | 'value' | 'defaultValue'> {
  onSearch: (data: IListSearchPayload) => void;
  defaultValue?: string;
  mode?: ListSearchMode;
  minChars?: number;
  debounceMs?: number;
  sx?: SxProps<Theme>;
  loading?: boolean;
}

const ListSearchFieldBase: ForwardRefRenderFunction<HTMLDivElement, IListSearchFieldProps> = (
  {
    sx = {},
    onSearch,
    mode = 'debounced',
    minChars = mode === 'debounced' ? 3 : 0,
    debounceMs = mode === 'debounced' ? 500 : 300,
    placeholder = 'Pesquisar',
    size = 'small',
    defaultValue = '',
    loading = false,
    ...rest
  },
  ref
) => {
  const [value, setValue] = useState(defaultValue);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);

    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }

    typingTimeout.current = setTimeout(() => {
      const searchBy = newValue.length >= minChars ? newValue : '';
      onSearchRef.current({ page: 1, searchBy });
    }, debounceMs);
  };

  return (
    <TextField
      ref={ref}
      size={size}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      sx={{ width: '100%', ...sx }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: loading ? (
          <InputAdornment position="end">
            <CircularProgress size={18} />
          </InputAdornment>
        ) : undefined,
      }}
      {...rest}
    />
  );
};

export const ListSearchField = forwardRef(ListSearchFieldBase);
export type { IListSearchFieldProps };
